/**
 * A set's `context.startMs` is the recording station's wall clock at capture
 * time, taken verbatim from the archive's `context.json`. A station with a
 * mis-set date (dead RTC, wrong year) stamps its games days or years off, and
 * because a tournament's displayed date and catalog sort position are the
 * `Math.max` of its sets' `startMs`, a single future-dated outlier hijacks both.
 *
 * These helpers ignore timestamps beyond a small skew from "now" so one bad
 * clock can't drag a 2026 tournament into 2028.
 */

/** Max clock skew past the present we still treat as a real timestamp. */
export const MAX_START_MS_SKEW = 24 * 60 * 60 * 1000; // 24h

/** Whether `ms` is a usable epoch-ms timestamp (positive, not far in the future). */
export function isPlausibleStartMs(ms: number, now: number = Date.now()): boolean {
  return Number.isFinite(ms) && ms > 0 && ms <= now + MAX_START_MS_SKEW;
}

/**
 * Newest plausible `startMs` among `values`, for a tournament's displayed date
 * and sort key. Future-dated outliers are skipped; if every value is an outlier
 * we fall back to the raw max so the tournament still sorts somewhere rather
 * than collapsing to 0.
 */
export function latestPlausibleStartMs(values: Iterable<number>, now: number = Date.now()): number {
  let max = 0;
  let plausibleMax = 0;
  for (const ms of values) {
    if (!Number.isFinite(ms) || ms <= 0) continue;
    if (ms > max) max = ms;
    if (ms <= now + MAX_START_MS_SKEW && ms > plausibleMax) plausibleMax = ms;
  }
  return plausibleMax || max;
}
